import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useGetGenericAccountParticipantByIdMutation,
  useGetTimeZoneList,
  useUpdateParticipantsGenericAccountByIdMutation,
} from "../../../api/services/client/clientApis";
import { UpdateParticipantDataParams } from "../../../api/services/client/models";
import {
  FormController,
  FormControllerCheckbox,
  MuiCheckbox,
  MuiDateFieldFormController,
  MuiDoubleLabelWithFormattedPhoneText,
  MuiSelect,
  MuiTextField,
  WarningConfirmationModal,
} from "../../../components";
import { getIsoMapCountryCode } from "../../../components/phoneLibrary/rawCountries";
import {
  EMPTY_OBJECT,
  MODULE_PERMISSIONS,
  PAGE_IDS,
  PAGE_URLS,
} from "../../../helpers/constants";
import { getDateFormatByCountry } from "../../../helpers/constants/countryDateFormat";
import { LabelValue } from "../../../helpers/types/common";
import {
  convertToUserFormat,
  convertToUTCFormat,
  formatApiDataToDropDownOptionOnlyEmail,
  getValueOrBlank,
  isNotEmpty,
  setApiDataToDropDownOption,
  setApiDataToDropDownOptionForTimeZone,
} from "../../../helpers/utils";
import { useForceUpdate } from "../../../hooks/useForceUpdate";
import { useNavigationSiteCore } from "../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../hooks/usePermissions";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { useToast } from "../../../hooks/useToast";
import {
  siteCoreContentLoadingSelector,
  userPreferredLanguageSelector,
} from "../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../redux/slices/appState/appStateSlice";
import { createGenericAccounts } from "../../genericAccounts/createGenericAccount/siteCore/SiteCoreKeys";
import { GENERIC_ACCOUNT_TAB_INDEX } from "../../genericAccounts/editGenericAccountTab/EditGenericAccountTab";
import { EditViewParticipantComponent } from "../components/editViewParticipantComponent/EditViewParticipantComponent";
import {
  EditParticipantGenericAccountFormKey,
  EmailNotifications,
  ParticipantDetails,
} from "../components/modelsAndSchemas/Models";
import {
  getParticipantPreferredPhoneNumber,
  getSelectedEmail,
  getSelectedLanguagePreference,
} from "../helper";
import { EditParticipantWrapper } from "./EditParticipant.style";

const pageId = PAGE_IDS.EDIT_PARTICIPANT;
export const EditParticipant = () => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const navigate = useNavigate();
  const { commonLabels, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const location = useLocation();
  const forceUpdate = useForceUpdate();
  const { hasModulePermission } = usePermissions();

  const [showCancelConfirmationModal, setShowCancelConfirmationModal] =
    useState(false);
  const [showUpdateConfirmationModal, setShowUpdateConfirmationModal] =
    useState(false);
  const [participantData, setParticipantData] = useState<ParticipantDetails>();
  const [languagePreferenceData, setLanguagePreferenceData] = useState<
    LabelValue[]
  >([]);
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const { control, getValues, setValue, formState, reset } = useForm({
    mode: "onBlur",
    defaultValues: {
      [EditParticipantGenericAccountFormKey.programEndDate]: "",
      [EditParticipantGenericAccountFormKey.timeZoneId]: "",
      [EditParticipantGenericAccountFormKey.city]: "",
      [EditParticipantGenericAccountFormKey.languagePreference]: "",
      [EditParticipantGenericAccountFormKey.smsNotificationsCheckbox]: false,
      [EditParticipantGenericAccountFormKey.emailNotificationsCheckbox]: false,
      [EditParticipantGenericAccountFormKey.sendEmailNotifications]: "",
    },
  });

  useEffect(() => {
    forceUpdate();
  }, [formState.touchedFields, formState.dirtyFields]);

  const userSelectedLanguage: string = useSelector(
    userPreferredLanguageSelector,
  );

  const {
    refetchWithParams: getTimeZoneList,
    data: timeZoneListData,
    isLoading: isLoadingTimeZoneList,
  } = useGetTimeZoneList();

  const {
    mutateAsync: getParticipantData,
    data,
    isPending: isPendingParticipantData,
  } = useGetGenericAccountParticipantByIdMutation();

  const {
    mutateAsync: updateParticipantData,
    isPending: isPendingUpdateParticipantData,
    isSuccess: isSuccessUpdateParticipantData,
  } = useUpdateParticipantsGenericAccountByIdMutation();

  const participantId = location?.state?.candidateId;
  const from = location?.state?.from;
  const genericAccountId = location?.state?.genericAccountId;
  const isFromViewParticipant = location?.state?.isFromViewParticipant;
  const isFromEditGenericAccount = location?.state?.isFromEditGenericAccount;

  useEffect(() => {
    if (participantId) getParticipantData(participantId);
  }, [participantId]);

  useEffect(() => {
    getTimeZoneList(userSelectedLanguage);
  }, [userSelectedLanguage]);

  const getLabel = (key: string) => {
    return fetchLabel(createGenericAccounts.registeredParticipantsTitle, key);
  };

  useEffect(() => {
    if (
      isNotEmpty(data) &&
      data?.platformDetails?.languagePreferences &&
      isNotEmpty(data?.platformDetails?.languagePreferences)
    ) {
      const languagePreferenceList = setApiDataToDropDownOption(
        data?.platformDetails?.languagePreferences,
      );
      setLanguagePreferenceData(languagePreferenceList);
    }
  }, [data]);

  const navigateToViewParticipant = () => {
    navigate(PAGE_URLS.VIEW_PARTICIPANT, {
      replace: true,
      state: {
        candidateId: participantId,
        from: from,
        genericAccountId: genericAccountId,
        isFromViewParticipant: isFromViewParticipant,
      },
    });
  };

  const navigateToViewGenericAccount = () => {
    navigate(PAGE_URLS.VIEW_GENERIC_ACCOUNT, {
      replace: true,
      state: {
        candidateId: participantId,
        from: from,
        genericAccountId: genericAccountId,
        isFromViewParticipant: isFromViewParticipant,
        tabIndex: GENERIC_ACCOUNT_TAB_INDEX.RegisteredParticipants,
      },
    });
  };

  const navigateToEditGenericAccount = () => {
    navigate(PAGE_URLS.EDIT_GENERIC_ACCOUNTS, {
      replace: true,
      state: {
        genericAccountId: genericAccountId,
        tabIndex: GENERIC_ACCOUNT_TAB_INDEX.RegisteredParticipants,
      },
    });
  };

  const navigateBack = () => {
    if (from === PAGE_IDS.REGISTERED_PARTICIPANT_TAB) {
      if (isFromViewParticipant) navigateToViewParticipant();
      else {
        if (isFromEditGenericAccount) navigateToEditGenericAccount();
        else navigateToViewGenericAccount();
      }
    } else {
      navigate(-1);
    }
  };

  const handleCancelClick = () => {
    if (isNotEmpty(formState.touchedFields)) {
      setShowCancelConfirmationModal(true);
      return;
    }
    navigateBack();
  };

  useEffect(() => {
    if (isSuccessUpdateParticipantData) {
      displayToast(
        getLabel(createGenericAccounts.registeredParticipantDetailsUpdated),
        "success",
      );
      reset(getValues());
    }
  }, [isSuccessUpdateParticipantData]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isPendingParticipantData ||
          isLoadingTimeZoneList ||
          isPendingUpdateParticipantData,
        message: commonLabels.loadingPleaseWait,
      }),
    );
  }, [
    isPendingParticipantData,
    isLoadingTimeZoneList,
    isPendingUpdateParticipantData,
  ]);

  const getParticipantInfoLabel = (key: string) => {
    return fetchLabel(createGenericAccounts.participantInformationTitle, key);
  };

  const getProgramInfoLabel = (key: string) => {
    return fetchLabel(createGenericAccounts.programInformationTitle, key);
  };

  const getPlatformDetailsLabel = (key: string) => {
    return fetchLabel(createGenericAccounts.platformDetailsTitle, key);
  };

  const getContactPreferencesLabel = (key: string) => {
    return fetchLabel(createGenericAccounts.contactPreferencesTitle, key);
  };

  const formatDateValue = (value?: string) => {
    return isNotEmpty(value) ? dayjs(value, userCountryDateFormat) : null;
  };

  const renderTimeZone = () => {
    return (
      <FormController
        control={control}
        name={EditParticipantGenericAccountFormKey.timeZoneId}
      >
        <MuiSelect
          id={`${pageId}-${EditParticipantGenericAccountFormKey.timeZoneId}`}
          label={getPlatformDetailsLabel(
            createGenericAccounts.platformDetailsTimeZone,
          )}
          options={setApiDataToDropDownOptionForTimeZone(timeZoneListData)}
          visibleDefaultSelectOption
          placeholder={commonLabels.selectPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    );
  };

  const renderCity = () => {
    return (
      <FormController
        control={control}
        name={EditParticipantGenericAccountFormKey.city}
      >
        <MuiTextField
          showCloseIcon
          id={`${pageId}-${EditParticipantGenericAccountFormKey.city}`}
          label={getPlatformDetailsLabel(
            createGenericAccounts.platformDetailsCityOfResidence,
          )}
          placeholder={commonLabels.inputPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    );
  };

  const renderLanguagePreference = () => {
    return (
      <FormController
        control={control}
        name={EditParticipantGenericAccountFormKey.languagePreference}
      >
        <MuiSelect
          id={`${pageId}-${EditParticipantGenericAccountFormKey.languagePreference}`}
          label={getPlatformDetailsLabel(
            createGenericAccounts.platformDetailsLanguagePreference,
          )}
          options={languagePreferenceData}
          visibleDefaultSelectOption
          placeholder={commonLabels.selectPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    );
  };

  const getFormattedProgramEndDate = (programEndDate?: string) => {
    return convertToUserFormat(programEndDate, userCountryDateFormat);
  };

  useEffect(() => {
    if (isNotEmpty(data) && !siteCoreLoading) {
      setValue(
        EditParticipantGenericAccountFormKey.programEndDate,
        getFormattedProgramEndDate(data?.programInformation?.programEndDate),
      );
      setValue(
        EditParticipantGenericAccountFormKey.timeZoneId,
        getValueOrBlank(data?.platformDetails?.timeZone?.id?.toLowerCase()),
      );

      setValue(
        EditParticipantGenericAccountFormKey.city,
        getValueOrBlank(data?.platformDetails?.city),
      );

      setValue(
        EditParticipantGenericAccountFormKey.languagePreference,
        getValueOrBlank(
          getSelectedLanguagePreference(
            data?.platformDetails?.languagePreferences,
          )?.id,
        ),
      );

      setValue(
        EditParticipantGenericAccountFormKey.smsNotificationsCheckbox,
        !!data?.contactPreferences?.smsNotifications,
      );

      setValue(
        EditParticipantGenericAccountFormKey.emailNotificationsCheckbox,
        !!data?.contactPreferences?.emailNotifications,
      );

      setValue(
        EditParticipantGenericAccountFormKey.sendEmailNotifications,
        getSelectedEmail(
          data?.contactPreferences
            ?.emailforNotifications as EmailNotifications[],
        ),
      );

      setParticipantData({
        participantInformation: {
          details: [
            {
              label: getParticipantInfoLabel(
                createGenericAccounts.participantInformationFirstName,
              ),
              value: data?.participantInformation?.firstName,
            },
            {
              label: getParticipantInfoLabel(
                createGenericAccounts.participantInformationLastName,
              ),
              value: data?.participantInformation?.lastName,
            },
            {
              label: getParticipantInfoLabel(
                createGenericAccounts.participantInformationPreferredName,
              ),
              value: data?.participantInformation?.preferredName,
            },
            {
              label: getParticipantInfoLabel(
                createGenericAccounts.participantInformationEmail,
              ),
              value: data?.participantInformation?.email,
            },
            {
              label: getParticipantInfoLabel(
                createGenericAccounts.participantInformationAlternateEmail,
              ),
              value: data?.participantInformation?.alternateEmail,
            },
            {
              label: getParticipantInfoLabel(
                createGenericAccounts.participantInformationPreferredNumber,
              ),
              value: getParticipantPreferredPhoneNumber(data),
            },
          ],
          hasPermission:
            hasModulePermission(
              MODULE_PERMISSIONS.GENERIC_ACCOUNT_ADD_PARTICIPANT_INFORMATION,
            ) ||
            hasModulePermission(
              MODULE_PERMISSIONS.GENERIC_ACCOUNT_EDIT_PARTICIPANT_INFORMATION,
            ),
        },
        programInformation: {
          details: [
            {
              label: getProgramInfoLabel(
                createGenericAccounts.programInformationPlatformPersona,
              ),
              value: data?.programInformation?.platformPersona,
            },
            {
              label: getProgramInfoLabel(
                createGenericAccounts.programInformationJobLanded,
              ),
              value: data?.programInformation?.isJobLanded
                ? commonLabels.yes
                : commonLabels.no,
            },
            {
              label: getProgramInfoLabel(
                createGenericAccounts.programInformationProgramEndDate,
              ),
              value: getFormattedProgramEndDate(
                data?.programInformation?.programEndDate,
              ),
              isEdit: true,
              render: () => {
                return (
                  <MuiDateFieldFormController
                    control={control}
                    name={EditParticipantGenericAccountFormKey.programEndDate}
                    id={`${pageId}-${EditParticipantGenericAccountFormKey.programEndDate}`}
                    value={formatDateValue(
                      getValues(
                        EditParticipantGenericAccountFormKey.programEndDate,
                      ),
                    )}
                    label={getProgramInfoLabel(
                      createGenericAccounts.programInformationProgramEndDate,
                    )}
                    valueFormat={userCountryDateFormat}
                    format={userCountryDateFormat}
                    disablePast
                    disableManualInput
                    onChange={(newValue) =>
                      setValue(
                        EditParticipantGenericAccountFormKey.programEndDate,
                        getValueOrBlank(`${newValue}`),
                      )
                    }
                    requiredARIALabel={commonARIALabel.required}
                    selectDateARIALabel={commonARIALabel.selectDate}
                  />
                );
              },
            },
          ],
          hasPermission:
            hasModulePermission(
              MODULE_PERMISSIONS.GENERIC_ACCOUNT_ADD_PROGRAM_INFORMATION,
            ) ||
            hasModulePermission(
              MODULE_PERMISSIONS.GENERIC_ACCOUNT_EDIT_PROGRAM_INFORMATION,
            ),
        },
        platformDetails: {
          details: [
            {
              label: getPlatformDetailsLabel(
                createGenericAccounts.platformDetailsTimeZone,
              ),
              value: data?.platformDetails?.timeZone?.id,
              isEdit: true,
              render: renderTimeZone,
            },
            {
              label: getPlatformDetailsLabel(
                createGenericAccounts.platformDetailsCityOfResidence,
              ),
              value: data?.platformDetails.city,
              isEdit: true,
              render: renderCity,
            },
            {
              label: getPlatformDetailsLabel(
                createGenericAccounts.platformDetailsLanguagePreference,
              ),
              value: getSelectedLanguagePreference(
                data?.platformDetails?.languagePreferences,
              )?.id,
              isEdit: true,
              render: renderLanguagePreference,
            },
          ],
          hasPermission:
            hasModulePermission(
              MODULE_PERMISSIONS.GENERIC_ACCOUNT_ADD_PLATFORM_DETAILS,
            ) ||
            hasModulePermission(
              MODULE_PERMISSIONS.GENERIC_ACCOUNT_EDIT_PLATFORM_DETAILS,
            ),
        },
        contactPreferences: {
          details: [
            // Conditionally add SMS notification checkbox if preferredContactNumber is not empty
            ...(isNotEmpty(data?.participantInformation?.preferredContactNumber)
              ? [
                  {
                    label: getContactPreferencesLabel(
                      createGenericAccounts.contactPreferencesTextSMSNotificationsCheckbox,
                    ),
                    value: data?.contactPreferences?.smsNotifications,
                    isEdit: true,
                    render: () => {
                      return (
                        <FormControllerCheckbox
                          control={control}
                          name={
                            EditParticipantGenericAccountFormKey.smsNotificationsCheckbox
                          }
                        >
                          <MuiCheckbox
                            checked={data?.contactPreferences?.smsNotifications}
                            checkboxLabel={getContactPreferencesLabel(
                              createGenericAccounts.contactPreferencesTextSMSNotificationsCheckbox,
                            )}
                          />
                        </FormControllerCheckbox>
                      );
                    },
                  },
                ]
              : []),
            {
              label: getContactPreferencesLabel(
                createGenericAccounts.contactPreferencesEmailNotificationsCheckbox,
              ),
              value: data?.contactPreferences?.emailNotifications,
              isEdit: true,
              render: () => {
                return (
                  <FormControllerCheckbox
                    control={control}
                    name={
                      EditParticipantGenericAccountFormKey.emailNotificationsCheckbox
                    }
                  >
                    <MuiCheckbox
                      checked={data?.contactPreferences?.emailNotifications}
                      checkboxLabel={getContactPreferencesLabel(
                        createGenericAccounts.contactPreferencesEmailNotificationsCheckbox,
                      )}
                    />
                  </FormControllerCheckbox>
                );
              },
            },
            {
              ...EMPTY_OBJECT,
              isEdit: true,
              render: () => {
                return <></>;
              },
            },
            // Conditionally add SMS notification checkbox if preferredContactNumber is not empty
            ...(isNotEmpty(data?.participantInformation?.preferredContactNumber)
              ? [
                  {
                    label: getContactPreferencesLabel(
                      createGenericAccounts.sendTextSMSNotificationsTo,
                    ),
                    value: getParticipantPreferredPhoneNumber(data),
                    isEdit: true,
                    render: () => {
                      return (
                        <MuiTextField
                          id={`${pageId}-sendTextSmsNotifications`}
                          disabled
                          label={getContactPreferencesLabel(
                            createGenericAccounts.sendTextSMSNotificationsTo,
                          )}
                          requiredARIALabel={commonARIALabel.required}
                          startAdornment={
                            <MuiDoubleLabelWithFormattedPhoneText
                              label={""}
                              replaceEmptyWithDash
                              selectedCountry={getIsoMapCountryCode(
                                data?.participantInformation
                                  ?.preferredContactNumberISOCode,
                              )}
                              phoneNumber={
                                data?.participantInformation
                                  ?.preferredContactNumber
                              }
                              countryCode={
                                data?.participantInformation
                                  ?.preferredContactNumberISOCode
                              }
                              valueTypoVariant="h2Medium"
                              valueClasses={"sendTextSMSNotifications"}
                            />
                          }
                        />
                      );
                    },
                  },
                ]
              : [
                  {
                    ...EMPTY_OBJECT,
                    isEdit: true,
                    render: () => {
                      return <></>;
                    },
                  },
                ]),
            {
              label: getContactPreferencesLabel(
                createGenericAccounts.sendEmailNotificationsToDropdown,
              ),
              value: getSelectedEmail(
                data?.contactPreferences
                  ?.emailforNotifications as EmailNotifications[],
              ),
              isEdit: true,
              render: () => {
                return (
                  <FormController
                    control={control}
                    name={
                      EditParticipantGenericAccountFormKey.sendEmailNotifications
                    }
                  >
                    <MuiSelect
                      id={`${pageId}-${EditParticipantGenericAccountFormKey.sendEmailNotifications}`}
                      label={getContactPreferencesLabel(
                        createGenericAccounts.sendEmailNotificationsToDropdown,
                      )}
                      options={formatApiDataToDropDownOptionOnlyEmail(
                        data?.contactPreferences?.emailforNotifications,
                      )}
                      visibleDefaultSelectOption
                      placeholder={commonLabels.selectPlaceHolder}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                );
              },
            },
          ],
          hasPermission:
            hasModulePermission(
              MODULE_PERMISSIONS.GENERIC_ACCOUNT_EDIT_CONTACT_PREFERENCES,
            ) ||
            hasModulePermission(
              MODULE_PERMISSIONS.GENERIC_ACCOUNT_ADD_CONTACT_PREFERENCES,
            ),
        },
      });
    }
  }, [data, languagePreferenceData, siteCoreLoading, timeZoneListData]);

  const handleUpdateParticipantClick = () => {
    setShowUpdateConfirmationModal(true);
  };

  const getValuesAndUpdateParticipantData = () => {
    const {
      city,
      emailNotificationsCheckbox,
      sendEmailNotifications,
      languagePreference,
      programEndDate,
      smsNotificationsCheckbox,
      timeZoneId,
    } = getValues();

    const payload: UpdateParticipantDataParams = {
      participantId: participantId,
      ...(programEndDate && {
        programEndDate: programEndDate
          ? convertToUTCFormat(programEndDate, userCountryDateFormat)
          : "",
      }),
      platformDetails: {
        ...(timeZoneId && { timeZoneId: timeZoneId }),
        ...(city && { city: city }),
        ...(languagePreference && { languagePreferenceId: languagePreference }),
      },
      contactPreferences: {
        smsNotifications: smsNotificationsCheckbox,
        emailNotifications: emailNotificationsCheckbox,
        ...(sendEmailNotifications && {
          emailIdforNotifications: sendEmailNotifications,
        }),
      },
    };

    updateParticipantData(payload);
  };

  const handleCancelModalClose = () => {
    if (showCancelConfirmationModal || showUpdateConfirmationModal) {
      setShowCancelConfirmationModal(false);
      setShowUpdateConfirmationModal(false);
    }
  };

  const handleCancelModalPrimaryButtonClick = () => {
    setShowCancelConfirmationModal(false);
    navigateBack();
  };

  const handleUpdateModalPrimaryButtonClick = () => {
    setShowUpdateConfirmationModal(false);
    getValuesAndUpdateParticipantData();
  };

  return (
    <EditParticipantWrapper>
      <EditViewParticipantComponent
        participantData={participantData}
        headerTitle={getLabel(createGenericAccounts.editParticipant)}
        primaryButtonTitle={commonLabels.update}
        secondaryButtonTitle={commonLabels.close}
        showEditButtonInHeader={false}
        onPrimaryButtonClick={handleUpdateParticipantClick}
        onSecondaryButtonClick={handleCancelClick}
        participantId={getValueOrBlank(data?.participantId)}
        isEditProgram={false}
      />
      {showCancelConfirmationModal && (
        <WarningConfirmationModal
          labelContent={commonLabels.cancelButtonActionText}
          show={showCancelConfirmationModal}
          onClose={handleCancelModalClose}
          onPrimaryButtonClick={handleCancelModalPrimaryButtonClick}
          primaryButtonTitle={commonLabels.yes}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
      {showUpdateConfirmationModal && (
        <WarningConfirmationModal
          labelContent={getLabel(
            createGenericAccounts.areYouSureYouWantToUpdateTheParticipantsDetails,
          )}
          show={showUpdateConfirmationModal}
          modalTitle={commonLabels.confirmation}
          onClose={handleCancelModalClose}
          onPrimaryButtonClick={handleUpdateModalPrimaryButtonClick}
          primaryButtonTitle={commonLabels.yes}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
    </EditParticipantWrapper>
  );
};
